import { Injectable } from '@angular/core';

import {
  IAlerta,
  IBombeoArrancador,
  IBombeoCanal,
  IDispositivo,
  IDispositivoBombeoV2,
  IDispositivoWithRegistros,
  IListado,
  IQueryParam,
  IReporteBombeoArrancadorV2,
  IReporteBombeoVariadorV2,
  IUpdateBombeoArrancador,
  IUpdateBombeoCanal,
} from 'modelos/src';
import { Observable } from 'rxjs';
import { apiBase } from 'src/environments/environment';
import { HelperService } from '../../../auxiliares/helper.service';
import { HttpService } from '../../../auxiliares/http.service';

const api = apiBase;

export interface IAlertaSSW {
  tieneError: boolean;
  tieneAlarma: boolean;
  tipo: 'SSW06' | 'SSW07' | 'SSW900' | 'CFW11' | string;
  estadoActual?: {
    codigo: number;
    descripcion: string;
  };
  errorActual?: {
    codigo: number;
    descripcion: string;
  };
  alarmaActual?: {
    codigo: number;
    descripcion: string;
  };
}

export interface IAlertaCanales {
  enAlertaEntrada: boolean;
  enAlertaSalida: boolean;
  razonEntrada: string;
  razonSalida: string;
  detalles: {
    nivelEntrada?: number;
    nivelSalida?: number;
    nivelEntradaFormateado?: string;
    nivelSalidaFormateado?: string;
    frecuenciaAltaEntrada?: boolean;
    frecuenciaAltaSalida?: boolean;
  };
}

export interface IAlertaGeneral {
  enAlerta: boolean;
  razon: string;
  detalles: any;
}

@Injectable({
  providedIn: 'root',
})
export class BombeoV2Service {
  constructor(private http: HttpService) {}

  public listarWithRegistros(
    queryParams?: IQueryParam,
  ): Observable<IListado<IDispositivoWithRegistros>> {
    const params = HelperService.getQueryParams(queryParams);
    return this.http.get(`${api}/dispositivos/bombeo/with-reports`, { params });
  }
  // Canal
  public getCanal(id: string): Observable<IBombeoCanal> {
    return this.http.get(`${api}/bombeocanals/${id}`);
  }
  public getCanales(queryParams?: IQueryParam): Observable<{
    totalCount: number;
    datos: IBombeoCanal[];
  }> {
    const params = HelperService.getQueryParams(queryParams);
    return this.http.get(`${api}/bombeocanals`, { params });
  }
  public delCanal(id: string): Observable<IBombeoCanal> {
    return this.http.delete(`${api}/bombeocanals/${id}`);
  }
  public updateCanal(
    id: string,
    data: IUpdateBombeoCanal,
  ): Observable<IBombeoCanal> {
    return this.http.put(`${api}/bombeocanals/${id}`, data);
  }

  // Arrancador
  public getArrancador(id: string): Observable<IBombeoArrancador> {
    return this.http.get(`${api}/bombeoarrancadors/${id}`);
  }
  public getArrancadores(queryParams?: IQueryParam): Observable<{
    totalCount: number;
    datos: IBombeoArrancador[];
  }> {
    const params = HelperService.getQueryParams(queryParams);
    return this.http.get(`${api}/bombeoarrancadors`, { params });
  }
  public delArrancador(id: string): Observable<IBombeoArrancador> {
    return this.http.delete(`${api}/bombeoarrancadors/${id}`);
  }

  public updateArrancador(
    id: string,
    data: IUpdateBombeoArrancador,
  ): Observable<IBombeoArrancador> {
    return this.http.put(`${api}/bombeoarrancadors/${id}`, data);
  }

  public async exportarBomba(
    filename: string,
    queryParams?: IQueryParam,
  ): Promise<void> {
    const params = HelperService.getQueryParams(queryParams);

    await this.http.getFile(
      `${api}/reportes/exportar/bombeo`,
      filename,
      params,
    );
  }

  public async exportarCanal(
    filename: string,
    queryParams?: IQueryParam,
  ): Promise<void> {
    const params = HelperService.getQueryParams(queryParams);
    await this.http.getFile(
      `${api}/reportes/exportar/bombeo-canal`,
      filename,
      params,
    );
  }

  public async exportarAlertasCanal(
    filename: string,
    queryParams?: IQueryParam,
  ): Promise<void> {
    const params = HelperService.getQueryParams(queryParams);
    await this.http.getFile(
      `${api}/alertas/exportar/bombeo-canal`,
      filename,
      params,
    );
  }

  public getUltimosReportes(
    deveui: string,
  ): Observable<{ bombas: { i: any }[] }> {
    return this.http.get(
      `${api}/dispositivos/bombeo/${deveui}/ultimosRegistros`,
    );
  }

  // Comandos

  public automatico(deveui: string, automatico: boolean) {
    return this.http.post(`${api}/comandos/bombeo-v2/automatico`, {
      deveui,
      automatico,
    });
  }

  public levantarFalla(deveui: string, numero: number) {
    return this.http.post(
      `${api}/comandos/bombeo-v2/levantarFallasElectricas`,
      {
        deveui,
        numero,
      },
    );
  }

  public encender(deveui: string, encender: boolean, numero: number) {
    return this.http.post(`${api}/comandos/bombeo-v2/encender`, {
      deveui,
      encender,
      numero,
    });
  }

  public habilitar(deveui: string, habilitar: boolean, numero: number) {
    return this.http.post(`${api}/comandos/bombeo-v2/habilitar`, {
      deveui,
      habilitar,
      numero,
    });
  }

  public pedirConfigsTotales(deveui: string) {
    return this.http.post(`${api}/comandos/bombeo-v2/pedirConfigs`, {
      deveui,
    });
  }

  public pedirConfigsGenerales(deveui: string) {
    return this.http.post(`${api}/comandos/bombeo-v2/pedirResumenConfigs`, {
      deveui,
    });
  }

  public cambiarTiempoSinReportar(
    deveui: string,
    tipo: string,
    tiempo: number,
  ) {
    return this.http.post(
      `${api}/comandos/bombeo-v2/cambiarTiempoSinReportar`,
      {
        deveui,
        tipo,
        tiempo,
      },
    );
  }

  public cambiarConfigNivel(
    deveui: string,
    tipo: string,
    numero: number,
    valor: number,
  ) {
    return this.http.post(`${api}/comandos/bombeo-v2/cambiarConfigNivel`, {
      deveui,
      tipo,
      numero,
      valor,
    });
  }

  public prioridad(deveui: string, prioridades: { [key: number]: number }) {
    return this.http.post(`${api}/comandos/bombeo-v2/prioridad`, {
      deveui,
      prioridades,
    });
  }

  public cambioVelocidad(
    deveui: string,
    numeroArrancador: number,
    velocidad: number,
  ) {
    return this.http.post(`${api}/comandos/bombeo-v2/cambioVelocidad`, {
      deveui,
      numeroArrancador,
      velocidad,
    });
  }

  public iniciarModoTest(deveui: string) {
    return this.http.post(`${api}/comandos/bombeo-v2/iniciarModoTest`, {
      deveui,
    });
  }

  public getAlertaNivelesCanales(
    dispositivo: IDispositivo,
    alertas: IAlerta[] = [],
  ): IAlertaCanales {
    const config = dispositivo.config as IDispositivoBombeoV2;
    const ahora = new Date();

    // Estructura de resultado
    const resultado: IAlertaCanales = {
      enAlertaEntrada: false,
      enAlertaSalida: false,
      razonEntrada: '',
      razonSalida: '',
      detalles: {
        nivelEntrada: undefined,
        nivelSalida: undefined,
        nivelEntradaFormateado: '',
        nivelSalidaFormateado: '',
        frecuenciaAltaEntrada: false,
        frecuenciaAltaSalida: false,
      },
    };

    // ---- CANAL DE ENTRADA ----
    if (
      config &&
      config.hayEntrada &&
      config.nivelCanalEntrada !== undefined &&
      config.nivelCanalEntrada !== null
    ) {
      // Almacenar nivel bruto
      resultado.detalles.nivelEntrada = config.nivelCanalEntrada;

      // Calcular nivel de agua formateado para canal de entrada
      let nivelAguaMM;
      if (config.igmEntrada !== undefined && config.igmEntrada !== null) {
        nivelAguaMM = config.igmEntrada - config.nivelCanalEntrada;
      } else {
        const maxMm = 10000; // Máximo en milímetros
        nivelAguaMM = maxMm - config.nivelCanalEntrada;
      }
      const nivelAguaM = nivelAguaMM / 1000;
      const nivelFormateado = `${nivelAguaM.toFixed(2)}m`;

      // Guardar nivel formateado
      resultado.detalles.nivelEntradaFormateado = nivelFormateado;

      // Verificar si hay alerta por nivel bajo (nivel < 400)
      if (config.nivelCanalEntrada < 400) {
        resultado.enAlertaEntrada = true;
        resultado.razonEntrada = `Alerta en canal de entrada (${nivelFormateado})`;
      } else {
        // Aunque no hay alerta por nivel, guardamos el nivel formateado para mostrarlo
        resultado.razonEntrada = `Canal de entrada: ${nivelFormateado}`;
      }

      // Analizar patrones de alertas
      if (alertas && alertas.length > 0) {
        // Filtrar alertas solo de tipo "Entrada"
        const alertasEntrada = alertas.filter((alerta) => {
          return alerta.reportes?.some(
            (reporte) => (reporte as any).tipo === 'Entrada',
          );
        });

        if (alertasEntrada.length > 0) {
          // Ordenar alertas por fecha, la más reciente primero
          const alertasOrdenadas = alertasEntrada.sort(
            (a, b) =>
              new Date(b.fecha!).getTime() - new Date(a.fecha!).getTime(),
          );

          // Definir ventana de tiempo para análisis (1 hora)
          const unaHoraMs = 60 * 60 * 1000;

          // Filtrar alertas de entrada de la última hora y del mismo dia
          const alertasRecientes = alertasOrdenadas.filter((alerta) => {
            if (!alerta.fecha) return false;
            const fechaAlerta = new Date(alerta.fecha);

            // Verificar que sea del mismo día
            const mismoDia =
              fechaAlerta.getDate() === ahora.getDate() &&
              fechaAlerta.getMonth() === ahora.getMonth() &&
              fechaAlerta.getFullYear() === ahora.getFullYear();

            if (!mismoDia) return false;

            return ahora.getTime() - fechaAlerta.getTime() <= unaHoraMs;
          });

          const alertaRecienteHoy =
            alertasRecientes.length > 0 ? alertasRecientes[0] : null;

          // Si hay 4 o más alertas recientes
          if (alertaRecienteHoy) {
            // Contar ocurrencias de alertas por tipo
            const contadorTipos: { [key: string]: number } = {};
            alertasRecientes.forEach((alerta) => {
              if (alerta.reportes && alerta.reportes.length > 0) {
                const tipo = (alerta.reportes[0] as any).tipo;
                if (tipo) {
                  contadorTipos[tipo] = (contadorTipos[tipo] || 0) + 1;
                }
              }
            });

            // Extraer el tipo y mensaje específico
            const reporte =
              alertaRecienteHoy.reportes && alertaRecienteHoy.reportes[0];
            if (reporte) {
              const tipoDispositivo = (reporte as any).tipo;
              const mensaje = (reporte as any).mensaje;

              if (tipoDispositivo && mensaje) {
                // Verificar si hay una frecuencia alta de un mismo tipo de alerta
                const frecuenciaTipo = contadorTipos[tipoDispositivo] || 0;
                let debeActivarAlerta = false;

                // 1. Determinar si debe activar la alerta
                if (frecuenciaTipo >= 4 && tipoDispositivo === 'Entrada') {
                  debeActivarAlerta = true;
                }

                // 2. Si debe activar la alerta, configurar los detalles y el mensaje
                if (debeActivarAlerta) {
                  resultado.enAlertaEntrada = true;

                  // Configurar detalles adicionales si es por frecuencia alta
                  if (frecuenciaTipo >= 4) {
                    resultado.detalles.frecuenciaAltaEntrada = true;
                  }

                  // Personalizar mensaje según el tipo de dispositivo
                  resultado.razonEntrada = `Alerta en Canal ${tipoDispositivo}: ${mensaje}`;
                }
              }
            }
          }
        }
      }
    }

    // ---- CANAL DE SALIDA ----
    if (
      config &&
      config.haySalida &&
      config.nivelCanalSalida !== undefined &&
      config.nivelCanalSalida !== null
    ) {
      // Almacenar nivel bruto
      resultado.detalles.nivelSalida = config.nivelCanalSalida;

      // Calcular nivel de agua formateado para canal de salida
      let nivelAguaMM;
      if (config.igmSalida !== undefined && config.igmSalida !== null) {
        nivelAguaMM = config.igmSalida - config.nivelCanalSalida;
      } else {
        const maxMm = 10000; // Máximo en milímetros
        nivelAguaMM = maxMm - config.nivelCanalSalida;
      }
      const nivelAguaM = nivelAguaMM / 1000;
      const nivelFormateado = `${nivelAguaM.toFixed(2)}m`;

      // Guardar nivel formateado
      resultado.detalles.nivelSalidaFormateado = nivelFormateado;

      // Verificar si hay alerta por nivel bajo (nivel < 400)
      if (config.nivelCanalSalida < 400) {
        resultado.enAlertaSalida = true;
        resultado.razonSalida = `Alerta en canal de salida (${nivelFormateado})`;
      } else {
        // Aunque no hay alerta por nivel, guardamos el nivel formateado para mostrarlo
        resultado.razonSalida = `Canal de salida: ${nivelFormateado}`;
      }

      // Analizar patrones de alertas
      if (alertas && alertas.length > 0) {
        // Filtrar alertas solo de tipo "Salida"
        const alertasSalida = alertas.filter((alerta) => {
          return alerta.reportes?.some(
            (reporte) => (reporte as any).tipo === 'Salida',
          );
        });

        if (alertasSalida.length > 0) {
          // Ordenar alertas por fecha, la más reciente primero
          const alertasOrdenadas = alertasSalida.sort(
            (a, b) =>
              new Date(b.fecha!).getTime() - new Date(a.fecha!).getTime(),
          );

          // Definir ventana de tiempo para análisis (1 hora)
          const unaHoraMs = 60 * 60 * 1000;

          // Filtrar alertas de salida de la última hora
          const alertasRecientes = alertasOrdenadas.filter((alerta) => {
            if (!alerta.fecha) return false;
            const fechaAlerta = new Date(alerta.fecha);
            // Verificar que sea del mismo día
            const mismoDia =
              fechaAlerta.getDate() === ahora.getDate() &&
              fechaAlerta.getMonth() === ahora.getMonth() &&
              fechaAlerta.getFullYear() === ahora.getFullYear();

            if (!mismoDia) return false;
            return ahora.getTime() - fechaAlerta.getTime() <= unaHoraMs;
          });

          const alertaRecienteHoy =
            alertasRecientes.length > 0 ? alertasRecientes[0] : null;

          // Si hay 4 o más alertas recientes
          if (alertaRecienteHoy) {
            // Contar ocurrencias de alertas por tipo
            const contadorTipos: { [key: string]: number } = {};
            alertasRecientes.forEach((alerta) => {
              if (alerta.reportes && alerta.reportes.length > 0) {
                const tipo = (alerta.reportes[0] as any).tipo;
                if (tipo) {
                  contadorTipos[tipo] = (contadorTipos[tipo] || 0) + 1;
                }
              }
            });

            // Extraer el tipo y mensaje específico
            const reporte =
              alertaRecienteHoy.reportes && alertaRecienteHoy.reportes[0];
            if (reporte) {
              const tipoDispositivo = (reporte as any).tipo;
              const mensaje = (reporte as any).mensaje;

              if (tipoDispositivo && mensaje) {
                // Verificar si hay una frecuencia alta de un mismo tipo de alerta
                const frecuenciaTipo = contadorTipos[tipoDispositivo] || 0;
                let debeActivarAlerta = false;

                // 1. Determinar si debe activar la alerta
                if (frecuenciaTipo >= 4 && tipoDispositivo === 'Salida') {
                  debeActivarAlerta = true;
                }

                // 2. Si debe activar la alerta, configurar los detalles y el mensaje
                if (debeActivarAlerta) {
                  resultado.enAlertaSalida = true;

                  // Configurar detalles adicionales si es por frecuencia alta
                  if (frecuenciaTipo >= 4) {
                    resultado.detalles.frecuenciaAltaSalida = true;
                  }

                  // Personalizar mensaje según el tipo de dispositivo
                  resultado.razonSalida = `Alerta en Canal ${tipoDispositivo}: ${mensaje}`;
                }
              }
            }
          }
        }
      }
    }

    const alertaGeneralCentral = this.getAlertaCentral(dispositivo);

    if (alertaGeneralCentral.enAlerta) {
      // Si hay alerta general, solo cambiamos las alertas de resultados
      resultado.enAlertaEntrada = true;
      resultado.enAlertaSalida = true;
      resultado.razonEntrada = alertaGeneralCentral.razon;
      resultado.razonSalida = alertaGeneralCentral.razon;
    }

    return resultado;
  }

  public getAlertaCentral(dispositivo: IDispositivo): IAlertaGeneral {
    const ahora = new Date();
    // Estructura de resultado
    const resultado: IAlertaGeneral = {
      enAlerta: false,
      razon: '',
      detalles: {} as any,
    };

    if (dispositivo.fechaUltimaComunicacion) {
      const fechaUltimaComunicacion = new Date(
        dispositivo.fechaUltimaComunicacion,
      );
      const tiempoSinComunicacion =
        ahora.getTime() - fechaUltimaComunicacion.getTime();
      // si pasaron mas de 6 horas sin comunicacion es una alerta
      const seisHorasMs = 6 * 60 * 60 * 1000; // 6 horas en milisegundos
      if (tiempoSinComunicacion > seisHorasMs) {
        resultado.enAlerta = true;
        resultado.razon = 'Central sin comunicación por más de 6 horas';
        resultado.detalles.fechaUltimaComunicacion =
          dispositivo.fechaUltimaComunicacion;
      }
    }

    return resultado;
  }

  // determinar alerta general de semaforos
  public getAlertaGeneral(
    dispositivo: IDispositivo,
    alertas: IAlerta[],
    reportesDispositivo: Record<string, any>,
  ): IAlertaGeneral | null {
    if (!dispositivo) {
      console.log('⚠️ Dispositivo es null/undefined en getAlertaGeneral');
      return {
        enAlerta: false,
        razon: '',
        detalles: {},
      };
    }

    const config = dispositivo.config as IDispositivoBombeoV2;
    const ahora = new Date();
    // Estructura de resultado
    const resultado = {
      enAlerta: false,
      razon: '',
      detalles: {} as any,
    };

    if (!config) {
      console.log('⚠️ Config es null después del cast en getAlertaGeneral');
      return {
        enAlerta: false,
        razon: '',
        detalles: {},
      };
    }

    // 1. Verificar alertas en niveles de canales
    if (
      config.hayEntrada &&
      config.nivelCanalEntrada !== undefined &&
      config.nivelCanalEntrada < 400
    ) {
      resultado.enAlerta = true;
      resultado.razon = 'Alerta en canal de entrada';
      resultado.detalles.nivelEntrada = config.nivelCanalEntrada;
    } else if (
      config.haySalida &&
      config.nivelCanalSalida !== undefined &&
      config.nivelCanalSalida < 400
    ) {
      resultado.enAlerta = true;
      resultado.razon = 'Alerta en canal de salida';
      resultado.detalles.nivelSalida = config.nivelCanalSalida;
    }

    if (alertas && alertas.length > 0) {
      // Ordenar alertas por fecha, la más reciente primero
      const alertasOrdenadas = alertas.sort(
        (a, b) => new Date(b.fecha!).getTime() - new Date(a.fecha!).getTime(),
      );

      // Obtener la alerta más reciente
      const alertaReciente = alertasOrdenadas[0];

      // Definir ventana de tiempo para análisis (1 hora)
      const unaHoraMs = 60 * 60 * 1000;

      // Filtrar alertas de la última hora y de
      const alertasRecientes = alertasOrdenadas.filter((alerta) => {
        if (!alerta.fecha) return false;
        const fechaAlerta = new Date(alerta.fecha);

        const mismoDia =
          fechaAlerta.getDate() === ahora.getDate() &&
          fechaAlerta.getMonth() === ahora.getMonth() &&
          fechaAlerta.getFullYear() === ahora.getFullYear();
        if (!mismoDia) return false;
        // luego nos fijamos si la diferencia de horas es menor a 1
        return ahora.getTime() - fechaAlerta.getTime() <= unaHoraMs;
      });

      const alertaRecienteHoy =
        alertasRecientes.length > 0 ? alertasRecientes[0] : null;

      // Contar ocurrencias de alertas por tipo
      const contadorTipos: { [key: string]: number } = {};
      alertasRecientes.forEach((alerta) => {
        if (alerta.reportes && alerta.reportes.length > 0) {
          const tipo = (alerta.reportes[0] as any).tipo;
          if (tipo) {
            contadorTipos[tipo] = (contadorTipos[tipo] || 0) + 1;
          }
        }
      });

      if (alertaRecienteHoy && alertaRecienteHoy.fecha) {
        const fechaAlerta = new Date(alertaRecienteHoy.fecha);
        const tiempoTranscurrido = ahora.getTime() - fechaAlerta.getTime();

        // Extraer el tipo y mensaje específico
        const reporte =
          alertaRecienteHoy.reportes && alertaRecienteHoy.reportes[0];
        if (reporte) {
          const tipoDispositivo = (reporte as any).tipo;
          const mensaje = (reporte as any).mensaje;

          if (tipoDispositivo && mensaje) {
            // Verificar si hay una frecuencia alta de un mismo tipo de alerta
            const frecuenciaTipo = contadorTipos[tipoDispositivo] || 0;
            let debeActivarAlerta = false;

            // 1. Determinar si debe activar la alerta
            if (
              frecuenciaTipo >= 4 &&
              tipoDispositivo !== 'Arrancador' &&
              tipoDispositivo !== 'Variador'
            ) {
              debeActivarAlerta = true;
            } else if (
              tiempoTranscurrido > unaHoraMs &&
              tipoDispositivo !== 'Arrancador' &&
              tipoDispositivo !== 'Variador'
            ) {
              debeActivarAlerta = true;
            }

            // 2. Si debe activar la alerta, configurar los detalles y el mensaje
            if (debeActivarAlerta) {
              resultado.enAlerta = true;

              // Configurar detalles adicionales si es por frecuencia alta
              if (frecuenciaTipo >= 4) {
                resultado.detalles.frecuenciaAlta = true;
                resultado.detalles.contadorAlertas = frecuenciaTipo;
              }

              // Personalizar mensaje según el tipo de dispositivo
              if (
                tipoDispositivo === 'Entrada' ||
                tipoDispositivo === 'Salida'
              ) {
                resultado.razon = `Alerta en Canal ${tipoDispositivo}: ${mensaje}`;
              } else {
                resultado.razon = `Alerta: ${mensaje}`;
              }
            } else if (
              tipoDispositivo === 'Arrancador' ||
              tipoDispositivo === 'Variador'
            ) {
              // Para arrancadores y variadores no mostrar nada
              resultado.enAlerta = false;
              resultado.razon = '';
            }

            // Guardar detalles para diagnóstico
            resultado.detalles.alertaReciente = alertaReciente;
            resultado.detalles.patronAlertas = contadorTipos;
          }
        }
      }
    }

    // 3. Verificar si hay alertas en los reportes de los SSW (arrancadores/variadores)
    if (reportesDispositivo && reportesDispositivo.estadosSSW) {
      // Iterar por todos los estados de SSW para encontrar alarmas o errores
      const estadosSSW = reportesDispositivo.estadosSSW;

      // Guardar información de los dispositivos con error para detalles
      const dispositivosConError: {
        numero: string;
        tipo: string;
        mensaje: string;
      }[] = [];

      // Buscar si algún dispositivo tiene error o alarma
      Object.keys(estadosSSW).forEach((numeroDispositivo) => {
        const estado = estadosSSW[numeroDispositivo];
        if (estado.tieneError || estado.tieneAlarma) {
          resultado.enAlerta = true;

          // Guardar detalles del dispositivo con error
          dispositivosConError.push({
            numero: numeroDispositivo,
            tipo: estado.tipo || 'Arrancador/Variador',
            mensaje: estado.tieneError
              ? `Error: ${estado.errorActual?.descripcion}`
              : `Alarma: ${estado.alarmaActual?.descripcion}`,
          });
        }
      });

      // Si hay dispositivos con error, añadir el primer error como razón principal
      if (dispositivosConError.length > 0) {
        const primerError = dispositivosConError[0];
        resultado.razon = `Error en Tablero Bomba n°${primerError.numero}`;

        // Guardar todos los dispositivos con error en los detalles
        resultado.detalles.dispositivosConError = dispositivosConError;
      }
    }

    // 4. Verificar si central reporto o esta sin comunicación
    const alertaCentral = this.getAlertaCentral(dispositivo);
    if (alertaCentral.enAlerta) {
      resultado.enAlerta = true;
      resultado.razon = alertaCentral.razon;
      resultado.detalles.alertaCentral = alertaCentral.detalles;
    }

    // 5. Verificar 220 voltaje (Encendido y apagado)
    if (!config.hayEnergia) {
      resultado.enAlerta = true;
      resultado.razon = 'Sin energía';
    }

    return resultado;
  }

  // Método principal para obtener información de alerta SSW de un dispositivo
  public getAlertaSSW(
    reporte: IReporteBombeoVariadorV2 | IReporteBombeoArrancadorV2,
  ): IAlertaSSW {
    // Inicializar resultado
    const resultado: IAlertaSSW = {
      tieneError: false,
      tieneAlarma: false,
      tipo: '',
    };

    // Si no hay reportes, devuelve el resultado vacío
    if (!reporte) {
      return resultado;
    }

    // Tomar el reporte más reciente
    const datoReporte = reporte;
    if (!datoReporte || !datoReporte) {
      return resultado;
    }
    resultado.tipo = datoReporte.tipo || '';

    // Procesar estado SSW
    if (datoReporte.estadoSSW !== undefined) {
      resultado.estadoActual = {
        codigo: datoReporte.estadoSSW,
        descripcion: this.estadoSSW(datoReporte.estadoSSW, datoReporte.tipo),
      };
    }

    // Procesar error actual
    if (datoReporte.errorActual !== undefined) {
      resultado.errorActual = {
        codigo: datoReporte.errorActual,
        descripcion: this.errorActualSSW(
          datoReporte.errorActual,
          datoReporte.tipo,
        ),
      };
      resultado.tieneError = datoReporte.errorActual > 0;
    }

    // Procesar alarma actual
    if (datoReporte.alarmaActual !== undefined) {
      resultado.alarmaActual = {
        codigo: datoReporte.alarmaActual,
        descripcion: this.alarmaActualSSW(
          datoReporte.alarmaActual,
          datoReporte.tipo,
        ),
      };
      resultado.tieneAlarma = datoReporte.alarmaActual > 0;
    }

    return resultado;
  }

  // Funciones auxiliares para interpretar códigos SSW
  private estadoSSW(
    estado: number,
    tipo?: 'SSW06' | 'SSW07' | 'SSW900' | 'CFW11',
  ): string {
    if (estado === undefined || estado === null) return '';

    const estadosSSW06: { [key: string]: string } = {
      '0': 'Listo para accionar el motor',
      '1': 'En test inicial de la red de alimentación y del motor',
      '2': 'Con error',
      '3': 'En rampa de aceleración',
      '4': 'En tensión plena',
      '5': 'Con contactor de by-pass accionado',
      '6': 'Reservado',
      '7': 'En rampa de desaceleración',
      '8': 'En frenado',
      '9': 'En cambio de sentido de giro',
      '10': 'En Jog',
      '11': 'En espera de tiempo de P630 (intervalo de tiempo después de parada)',
      '12': 'Con el habilita general deshabilitado',
    };

    const estadosSSW900: { [key: string]: string } = {
      '0': 'Listo para accionar el motor',
      '1': 'En test inicial de la red de alimentación y del motor',
      '2': 'Con falla',
      '3': 'En rampa de aceleración',
      '4': 'En tensión plena',
      '5': 'Con contactor de by-pass accionado',
      '6': 'Reservado',
      '7': 'En rampa de desaceleración',
      '8': 'En frenado',
      '9': 'En cambio de sentido de giro',
      '10': 'En Jog',
      '11': 'Tiempo antes del arranque',
      '12': 'Tiempo después del arranque',
      '13': 'Deshabilita general',
      '14': 'En modo configuración',
    };

    const estadosCFW11: { [key: string]: string } = {
      '0': 'Ready',
      '1': 'Run',
      '2': 'Subtensión',
      '3': 'Falla',
      '4': 'Autoajuste',
      '5': 'Configuración',
      '6': 'Frenado CC',
      '7': 'STO',
    };

    switch (tipo) {
      case 'SSW06':
        return estadosSSW06[estado.toString()] || `Desconocido (${estado})`;
      case 'SSW07':
        return estadosSSW06[estado.toString()] || `Desconocido (${estado})`;
      case 'SSW900':
        return estadosSSW900[estado.toString()] || `Desconocido (${estado})`;
      case 'CFW11':
        return estadosCFW11[estado.toString()] || `Desconocido (${estado})`;
      default:
        return `Estado ${estado} - Desconocido`;
    }
  }

  private errorActualSSW(
    errorActual: number,
    tipo?: 'SSW06' | 'SSW07' | 'SSW900' | 'CFW11',
  ): string {
    if (errorActual === undefined || errorActual === null) return '';
    if (errorActual === 0) return 'Sin error';

    const erroresSSW06: { [key: string]: string } = {
      '0': 'Sin error',
      '1': 'Error Comunicación Serie',
      '3': 'Desbalanceo de tensión en fases',
      '4': 'Sobretemperatura en la potencia',
      '5': 'Sobrecarga en el motor',
      '6': 'Error externo',
      '10': 'Error en la función copy',
      '11': 'Falta a tierra',
      '15': 'Motor no conectado',
      '16': 'Sobretensión',
      '18': 'Conexión equivocada al motor',
      '19': 'Cortocircuito en la potencia de la SSW',
      '24': 'Error de programación',
      '28': 'Error de timeout en la recepción de telegramas de la comunicación serial',
      '29': 'Error de comunicación Fieldbus inactiva',
      '30': 'Error de tarjeta de comunicación Fieldbus inactivo',
      '31': 'Fallo en la conexión de la HMI',
      '32': 'Sobretemperatura en el motor (DI6 = PTC)',
      '33': 'Sobretemperatura en el motor Ch1',
      '34': 'Sobretemperatura en el motor Ch2',
      '35': 'Sobretemperatura en el motor Ch3',
      '36': 'Sobretemperatura en el motor Ch4',
      '37': 'Sobretemperatura en el motor Ch5',
      '39': 'Sin la Tarjeta Opcional PT 106',
      '41': 'Error de autodiagnose durante el power-on',
      '43': 'Cable partido Ch1',
      '44': 'Cable partido Ch2',
      '45': 'Cable partido Ch3',
      '46': 'Cable partido Ch4',
      '47': 'Cable partido Ch5',
      '48': 'Cortocircuito Ch1',
      '49': 'Cortocircuito Ch2',
      '50': 'Cortocircuito Ch3',
      '51': 'Cortocircuito Ch4',
      '52': 'Cortocircuito Ch5',
      '57': 'Falla en los tiristores del brazo R-U',
      '58': 'Falla en los tiristores del brazo S-V',
      '59': 'Falla en los tiristores del brazo T-W',
      '62': 'Exeso de tiempo de limitación de corriente o par (torque) durante el arranque',
      '63': 'Rotor trabado en el final del arranque',
      '65': 'Subcorriente en el motor en régimen de tensión plena',
      '66': 'Sobrecorriente en el motor en régimen de tensión plena',
      '67': 'Secuencia de fase invertida en el inicio del arranque',
      '70': 'Subtensión en la electrónica',
      '71': 'Contacto del relé de By-pass abierto',
      '72': 'Sobrecorriente antes del cierre del By-Pass',
      '74': 'Desbalanceo de corriente',
      '75': 'Frecuencia de la red de alimentación fuera del rango permitido',
      '76': 'Subcorriente antes del cierre del By-pass',
      '77': 'Contacto del relé de By-pass cerrado',
      '78': 'Subtorque',
      '79': 'Sobretorque',
      '80': 'Subpotencia',
      '81': 'Sobrepotencia',
      '85': 'Sin SoftPLC',
      '86': 'Errores de usuario del SoftPLC',
      '87': 'Errores de usuario del SoftPLC',
      '88': 'Errores de usuario del SoftPLC',
      '89': 'Errores de usuario del SoftPLC',
    };

    const erroresSSW900: { [key: string]: string } = {
      '0': 'Sin error',
      '1': 'Desbalceamiento de Tensión en Motor',
      '2': 'Subtensión en la Alimentación del Motor',
      '3': 'Falta de Fase Arranque del Motor',
      '5': 'Sobrecarga Clase Térmica dell Motor',
      '10': 'Falla CCI',
      '15': 'Motor no conectado',
      '16': 'Sobretensión en la Alimentación del Motor',
      '18': 'Conexión al Motor Incorrecta',
      '19': 'Cortocircuito en Potencia: Motor Off',
      '20': 'Cortocircuito en Potencia: Motor On',
      '32': 'Sobretemperatura en el Motor DI6',
      '40': 'Falla Comunicación Serie CCI-CCM',
      '42': 'Falla en CPU (Watchdog)',
      '51': 'Temperatura Baja en los SCRs',
      '54': 'Sobrecalentamiento en los SCRs',
      '57': 'Falla en los SCRs R-U',
      '58': 'Falla en los SCRs S-V',
      '59': 'Falla en los SCRs T-W',
      '61': 'Tiempo entre Arranques',
      '62': 'Exeso Tiempo de Arranque',
      '63': 'Rotor Tablado',
      '64': 'Sobrecarga en los SCRs',
      '65': 'Subcorriente en el Motor',
      '66': 'Sobrecorriente en el Motor',
      '67': 'Fase Invertida RST/123',
      '68': 'Fase Invertida RST/132',
      '69': 'Tensión Incorrecta en la Alimentación de la Electrónica',
      '70': 'Subtensión en Circuito de Control',
      '71': 'Contacto de By-pass Abierto - No Cerrado',
      '72': 'Sobrecorriente Antes del By-Pass',
      '74': 'Desequilibrio de Corriente en Motor',
      '75': 'Frecuencia Fuera de Rango',
      '76': 'Subcorriente en By-pass',
      '77': 'Contactor de By-pass Cerrado - No Abierto',
      '78': 'Subtorque en el Motor',
      '79': 'Sobretorque en el Motor',
      '80': 'Subpotencia en el Motor',
      '81': 'Sobrepotencia en el Motor',
      '82': 'Falla Slot 1 - Accesorio Incompatible',
      '83': 'Falla Slot 2 - Accesorio Incompatible',
      '84': 'Falla de Autodiagnóstico',
      '85': 'Falla Slot 1 = Slot 2',
      '86': 'Falla Slot 1 - Configurado',
      '87': 'Falla Slot 2 - Configurado',
      '88': 'Falla Slot 1 - Conexión',
      '89': 'Falla Slot 2 - Conexión',
      '91': 'Falla Externa (DI)',
      '99': 'Offset Corriente Inválido',
      '101': 'Sobrecalentamiento del Motor Ch1',
      '102': 'Sobrecalentamiento del Motor Ch2',
      '103': 'Sobrecalentamiento del Motor Ch3',
      '104': 'Sobrecalentamiento del Motor Ch4',
      '105': 'Sobrecalentamiento del Motor Ch5',
      '106': 'Sobrecalentamiento del Motor Ch6',
      '109': 'Cable partido Ch1 - Temperatura Motor',
      '110': 'Cable partido Ch2 - Temperatura Motor',
      '111': 'Cable partido Ch3 - Temperatura Motor',
      '112': 'Cable partido Ch4 - Temperatura Motor',
      '113': 'Cable partido Ch5 - Temperatura Motor',
      '114': 'Cable partido Ch6 - Temperatura Motor',
      '117': 'Cortocircuito Ch1 - Temperatura Motor',
      '118': 'Cortocircuito Ch2 - Temperatura Motor',
      '119': 'Cortocircuito Ch3 - Temperatura Motor',
      '120': 'Cortocircuito Ch4 - Temperatura Motor',
      '121': 'Cortocircuito Ch5 - Temperatura Motor',
      '122': 'Cortocircuito Ch6 - Temperatura Motor',
      '127': 'Timeout Comunicación HMI',
      '128': 'Timeout Comunicación Serie',
      '129': 'Anybus Offline',
      '130': 'Falta de Acceso a Anybus',
      '131': 'Timeout Anybus Modbus TCP',
      '132': 'Anybus Idle',
      '133': 'Sin Alimentación en el Interfaz CAN',
      '134': 'Bus Off',
      '135': 'CANopen Offline',
      '136': 'Maestro en Idle',
      '137': 'Timeout en la Conexión DeviceNet',
      '708': 'La Aplicación SoftPLC No Está Rodando',
      '750': 'Falla o Alarma del SoftPLC',
    };

    const erroresCFW11: { [key: string]: string } = {
      '0': 'Sin error',
      '6': 'Desequilibrio o Falta de Fase en la Red',
      '21': 'Subtensión Link CC',
      '22': 'Sobretención Link CC',
      '30': 'Falla Brazo U',
      '34': 'Falla Brazo V',
      '38': 'Falla Brazo W',
      '42': 'Falla IGBT del Frenado',
      '48': 'Sobrecarga en los IGBTs',
      '51': 'Sobretemperatura IGBTs U',
      '54': 'Sobretemperatura IGBTs V',
      '57': 'Sobretemperatura IGBTs W',
      '62': 'Desequilibrio Térmico',
      '67': 'Cableado Invertido Encoder/Motor',
      '71': 'Sobrecorriente en la Salida',
      '72': 'Sobrecarga en el Motor',
      '74': 'Falta a la Tierra',
      '76': 'Desequilibrio de la Corriente del Motor',
      '77': 'Sobrecarga Resistor Frenagem',
      '78': 'Sobretemperatura Motor',
      '79': 'Falla Señales Encoder',
      '80': 'Falla en la CPU (Watchdog)',
      '82': 'Falla en la Función Copy',
      '84': 'Falla de Autodiagnose',
      '91': 'Falla Externa',
      '99': 'Offset Corriente Inválido',
      '150': 'Sobrevelocidad en el Motor',
      '151': 'Falla Módulo Memoria FLASH',
      '153': 'Sobretemperatura Aire Interno',
      '156': 'Subtemperatura',
      '160': 'Relés Parada de Seguridad',
      '161': 'Timeout LC11 CFW-11',
      '174': 'Falla Velocidad Ventilador Izquierda',
      '175': 'Falla Velocidad Ventilador Centro',
      '176': 'Falla Velocidad Ventilador Derecho',
      '179': 'Falla Velocidad Ventilador',
      '182': 'Falla Realimentación de Pulsos',
      '183': 'Sobrecarga IGBTs + Temperatura',
      '185': 'Falla Contactor Precarga',
      '186': 'Falla Temperatura Sensor 1',
      '187': 'Falla Temperatura Sensor 2',
      '188': 'Falla Temperatura Sensor 3',
      '189': 'Falla Temperatura Sensor 4',
      '190': 'Falla Temperatura Sensor 5',
      '228': 'Timeout Comunicación Serial',
      '229': 'Anybus Offline',
      '230': 'Error Acceso Anybus',
      '233': 'Sin Alimentación CAN',
      '234': 'Bus Off',
      '235': 'Error Comunicación CANopen',
      '236': 'Maestro en Idle',
      '237': 'Timeout Conexión DeviceNet',
      '238': 'Profibus Modo Clear',
      '239': 'Profibus Offline',
      '240': 'Error Acceso Interfaz Profibus',
      '416': 'Falla Desequilibrio Corriente IGBT',
      '418': 'Sobretemperatura en el Aire del Control',
      '701': 'HMI Desconectada',
    };

    switch (tipo) {
      case 'SSW06':
        return (
          erroresSSW06[errorActual.toString()] ||
          `Error desconocido (${errorActual})`
        );
      case 'SSW07':
        return (
          erroresSSW06[errorActual.toString()] ||
          `Error desconocido (${errorActual})`
        );
      case 'SSW900':
        return (
          erroresSSW900[errorActual.toString()] ||
          `Error desconocido (${errorActual})`
        );
      case 'CFW11':
        return (
          erroresCFW11[errorActual.toString()] ||
          `Error desconocido (${errorActual})`
        );
      default:
        return `Error ${errorActual} - Desconocido`;
    }
  }

  private alarmaActualSSW(
    alarmaActual: number,
    tipo?: 'SSW06' | 'SSW07' | 'SSW900' | 'CFW11',
  ): string {
    if (alarmaActual === undefined || alarmaActual === null) return '';
    if (alarmaActual === 0) return 'Sin alarma';

    const alarmasSSW06: { [key: string]: string } = {
      '0': 'Sin alarma',
      '5': 'Sobrecarga en el motor',
      '6': 'Error externo',
      '16': 'Sobretensión',
      '32': 'Sobretemperatura en el motor (DI6 = PTC)',
      '33': 'Sobretemperatura en el motor Ch1',
      '34': 'Sobretemperatura en el motor Ch2',
      '35': 'Sobretemperatura en el motor Ch3',
      '36': 'Sobretemperatura en el motor Ch4',
      '37': 'Sobretemperatura en el motor Ch5',
      '43': 'Cable partido Ch1',
      '44': 'Cable partido Ch2',
      '45': 'Cable partido Ch3',
      '46': 'Cable partido Ch4',
      '47': 'Cable partido Ch5',
      '48': 'Cortocircuito Ch1',
      '49': 'Cortocircuito Ch2',
      '50': 'Cortocircuito Ch3',
      '51': 'Cortocircuito Ch4',
      '52': 'Cortocircuito Ch5',
      '65': 'Subcorriente en el motor en régimen de tensión plena',
      '66': 'Sobrecorriente en el motor en régimen de tensión plena',
      '78': 'Subtorque',
      '79': 'Sobretorque',
      '80': 'Subpotencia',
      '81': 'Sobrepotencia',
      '90': 'Alarmas de usuario del SoftPLC',
      '91': 'Alarmas de usuario del SoftPLC',
      '92': 'Alarmas de usuario del SoftPLC',
      '93': 'Alarmas de usuario del SoftPLC',
    };

    const alarmasSSW900: { [key: string]: string } = {
      '0': 'Sin error',
      '1': 'Desbalceamiento de Tensión en Motor',
      '2': 'Subtensión en la Alimentación del Motor',
      '5': 'Sobrecarga Clase Térmica dell Motor',
      '16': 'Sobretensión en la Alimentación del Motor',
      '32': 'Sobretemperatura en el Motor DI6',
      '65': 'Subcorriente en el Motor',
      '66': 'Sobrecorriente en el Motor',
      '74': 'Desequilibrio de Corriente en Motor',
      '78': 'Subtorque en el Motor',
      '79': 'Sobretorque en el Motor',
      '80': 'Subpotencia en el Motor',
      '81': 'Sobrepotencia en el Motor',
      '90': 'Alarma Externa (DI)',
      '101': 'Sobrecalentamiento del Motor Ch1',
      '102': 'Sobrecalentamiento del Motor Ch2',
      '103': 'Sobrecalentamiento del Motor Ch3',
      '104': 'Sobrecalentamiento del Motor Ch4',
      '105': 'Sobrecalentamiento del Motor Ch5',
      '106': 'Sobrecalentamiento del Motor Ch6',
      '109': 'Cable partido Ch1 - Temperatura Motor',
      '110': 'Cable partido Ch2 - Temperatura Motor',
      '111': 'Cable partido Ch3 - Temperatura Motor',
      '112': 'Cable partido Ch4 - Temperatura Motor',
      '113': 'Cable partido Ch5 - Temperatura Motor',
      '114': 'Cable partido Ch6 - Temperatura Motor',
      '117': 'Cortocircuito Ch1 - Temperatura Motor',
      '118': 'Cortocircuito Ch2 - Temperatura Motor',
      '119': 'Cortocircuito Ch3 - Temperatura Motor',
      '120': 'Cortocircuito Ch4 - Temperatura Motor',
      '121': 'Cortocircuito Ch5 - Temperatura Motor',
      '122': 'Cortocircuito Ch6 - Temperatura Motor',
      '127': 'Timeout Comunicación HMI',
      '128': 'Timeout Comunicación Serie',
      '129': 'Anybus Offline',
      '131': 'Timeout Anybus Modbus TCP',
      '132': 'Anybus Idle',
      '133': 'Sin Alimentación en el Interfaz CAN',
      '134': 'Bus Off',
      '135': 'CANopen Offline',
      '136': 'Maestro en Idle',
      '137': 'Timeout en la Conexión DeviceNet',
      '177': 'Sustitución Ventilador',
      '181': 'Tensión Batería',
      '182': 'Reloj con Valor Inválido',
      '708': 'La Aplicación SoftPLC No Está Rodando',
      '750': 'Falla o Alarma del SoftPLC',
    };

    const alarmasCFW11: { [key: string]: string } = {
      '0': 'Sin error',
      '46': 'Carga Alta en el Motor',
      '47': 'Carga Alta en los IGBTs',
      '50': 'Temperatura IGBTs Alta U',
      '53': 'Temperatura IGBTs Alta V',
      '56': 'Temperatura IGBTs Alta W',
      '88': 'Comunicación Perdida',
      '90': 'Alarma Externa',
      '110': 'Temperatura Motor Alta',
      '128': 'Timeout Comunicación Serial',
      '129': 'Anybus Offline',
      '130': 'Error Acceso Anybus',
      '133': 'Sin Alimentación CAN',
      '134': 'Bus Off',
      '135': 'Error Comunicación CANopen',
      '136': 'Maestro en Idle',
      '137': 'Timeout Conexión Devicenet',
      '138': 'Interfaz Profibus DP en Modo Clear',
      '139': 'Interfaz Profibus DP Offline',
      '140': 'Error de Acceso al Módulo Profibus DP',
      '152': 'Temperatura Aire Interno Alta',
      '156': 'Subtemperatura',
      '162': 'Firmware PLC Incompatible',
      '163': 'Cable Partido AI1',
      '164': 'Cable Partido AI2',
      '165': 'Cable Partido AI3',
      '166': 'Cable Partido AI4',
      '177': 'Substitución Ventilador',
      '181': 'Reloj con Valor Inválido',
      '191': 'Alarma Temperatura Sensor 1',
      '192': 'Alarma Temperatura Sensor 2',
      '193': 'Alarma Temperatura Sensor 3',
      '194': 'Alarma Temperatura Sensor 4',
      '195': 'Alarma Temperatura Sensor 5',
      '196': 'Alarma Cable Sensor 1',
      '197': 'Alarma Cable Sensor 2',
      '198': 'Alarma Cable Sensor 3',
      '199': 'Alarma Cable Sensor 4',
      '200': 'Alarma Cable Sensor 5',
      '417': 'Desequilibrio Térmico',
      '419': 'Alarma Sobretemperatura en el Aire del Control',
      '700': 'HMI Desconectada',
      '702': 'Convertidor de frecuencia Deshabilitado',
      '704': 'Dos Movimientos Habilitados',
      '706': 'Referencia no Programada para la SoftPLC',
    };

    switch (tipo) {
      case 'SSW06':
        return (
          alarmasSSW06[alarmaActual.toString()] ||
          `Alarma desconocida (${alarmaActual})`
        );
      case 'SSW07':
        return (
          alarmasSSW06[alarmaActual.toString()] ||
          `Alarma desconocida (${alarmaActual})`
        );
      case 'SSW900':
        return (
          alarmasSSW900[alarmaActual.toString()] ||
          `Alarma desconocida (${alarmaActual})`
        );
      case 'CFW11':
        return (
          alarmasCFW11[alarmaActual.toString()] ||
          `Alarma desconocida (${alarmaActual})`
        );
      default:
        return `Alarma ${alarmaActual} - Desconocida`;
    }
  }
}
